import { UserTileInputDataProps } from '../../components/tiles/user-tile/user-tile.component';
import { AccountPermissionDTO } from 'src/common/dtos/account-permission.dto';
import { GranteeDTO } from 'src/common/dtos/grantee.dto';
import { OwnAccountDTO } from 'src/common/dtos/own-account.dto';
import { UserDetailsDto } from 'src/common/dtos/user-details.dto';
import { ClientType } from 'src/common/enums/client-type.enum';
import { Store } from '@ngrx/store';

import { RoleType } from '../../../common/constants/role-type.enum';
import { RegisteredRoleDTO } from '../../../common/dtos/account-roles.dto';
import { AccessTypeSelection } from '../../../common/enums/access-type-selection.enum';
import { ContentUtil } from '../content/content.util';
import { ObservableAccountTileInputDataProps } from '../sort/sort.service';
import { TokenValidationResponse } from '../../services/token-validation/token-validation.service';
import { AlmostThereResponse } from '../../../common/dtos/access-permissions.dto';
import { ErrorPage, errorPageMap } from '../../services/generic-error/error-page-utils';
import {
    updateAccessTypeInGranteeFlow,
    updateClientTypeInGranteeFlow,
    updateIsMultiAccountInGranteeFlow,
} from '../../store/actions/grantee-flow.actions';
import { ClientTypes } from '../../../common/enums/client-types.enum';
import { ErrorService } from '../../services/generic-error/error.service';
import { environment } from '../../../environments/environment';
import { PERMISSION_REQUEST_ERRORS } from '../../enums/permission_request_error.enum';
import { filterUsersToExistingGranteesAcrossAccounts } from '../../../common/utils/filter-users-to-existing-grantees.util';

/**
 * Extracts grantees from account data into a nested array structure.
 * @param accountData - Array of account DTOs
 * @returns Array of grantee arrays, one per account
 */
const getUsersFromAccountData = (accountData: OwnAccountDTO[]): GranteeDTO[][] => {
    return accountData?.map((a) => a.grantees);
};

/**
 * Flattens the nested grantee arrays into a single array of all grantees.
 * @param accountData - Array of account DTOs
 * @returns Flattened array of all grantees across all accounts
 */
const extractUsersFromAccountData = (accountData: OwnAccountDTO[]): GranteeDTO[] => {
    return [].concat.apply([], getUsersFromAccountData(accountData));
};

/**
 * Constructs a full name from user details by joining first, middle, and last names.
 * @param detail - User details containing name components
 * @returns Full name as a single string
 */
const constructUserName = (detail: UserDetailsDto) => {
    return [detail.firstName, detail.middleName, detail.lastName].reduce((acc, nxt) => {
        return [acc, nxt].join(' ');
    });
};

/**
 * Determines the appropriate contact person type label based on client type.
 * @param type - The client type (MANAGED, INDIVIDUAL, etc.)
 * @returns Localized label for the contact person type (IA, RM, or default)
 */
const getContactPersonType = (type: ClientType) => {
    switch (+type) {
        case ClientType.MANAGED:
            return ContentUtil.content.contactPersonType.ia;
        case ClientType.INDIVIDUAL:
            return ContentUtil.content.contactPersonType.rm;
        default:
            return ContentUtil.content.contactPersonType.default;
    }
};

/**
 * Filters accounts to find those associated with a specific client POID.
 * Checks both grantees and owners lists for the POID.
 * @param accounts - Array of account DTOs to search
 * @param clientPoid - The client POID to search for
 * @returns Array of accounts where the client is a grantee or owner
 */
const getAccountsByClientPoid = (accounts: OwnAccountDTO[], clientPoid: string) => {
    return accounts.filter(
        (acc) =>
            acc.grantees.filter((user) => user.clientPoid === clientPoid).length ||
            acc.owners.filter((user) => user.clientPoid === clientPoid).length,
    );
};

/**
 * Finds a specific user (grantee or co-owner) by their client POID.
 * @param grantees - Array of grantees to search
 * @param coOwners - Array of co-owners to search
 * @param clientPoid - The client POID to find
 * @returns The matching grantee or co-owner, or undefined if not found
 */
const getUserByClientPoid = (
    grantees: GranteeDTO[],
    coOwners: RegisteredRoleDTO[],
    clientPoid: string,
) => {
    return (
        grantees.find((grantee) => grantee.clientPoid === clientPoid) ||
        coOwners.find((coOwner) => coOwner.clientPoid === clientPoid)
    );
};

/**
 * Checks if a grantee is an HSG (Health Savings Group) recipient.
 * HSG recipients have role types IPHR (Healthcare Power) or IPIR (Healthcare Information).
 * @param grantees - Array of grantees to check
 * @param clientPoid - The client POID to check
 * @returns True if the client is an HSG recipient, false otherwise
 */
const checkIsHsgRecipient = (grantees: GranteeDTO[], clientPoid: string): boolean => {
    return grantees.some(
        (grantee) =>
            grantee.clientPoid === clientPoid &&
            (grantee.roleType === RoleType.IPHR || grantee.roleType === RoleType.IPIR),
    );
};

/**
 * Deduplicates grantees on an account by clientPoid and excludes the grantor's own clientPoid.
 * A grantee with multiple role-type entries (same clientPoid) should only be counted once,
 * and the grantor should never be counted as a grantee/user on their own account.
 * @param grantees - Array of grantees on an account
 * @param grantorClientPoid - The grantor's own clientPoid, excluded from the result if present
 * @returns Deduplicated array of grantees, excluding the grantor
 */
const getUniqueGrantees = (
    grantees: GranteeDTO[] | undefined,
    grantorClientPoid?: string,
): GranteeDTO[] => {
    if (!grantees?.length) {
        return [];
    }

    const seenPoids = new Set<string>();
    return grantees.filter((grantee) => {
        if (grantee.clientPoid === grantorClientPoid || seenPoids.has(grantee.clientPoid)) {
            return false;
        }
        seenPoids.add(grantee.clientPoid);
        return true;
    });
};

/**
 * Transforms observable account collection into a format suitable for account tile display.
 * @param obsAccounts - Array of account permission DTOs
 * @returns Array of transformed account tile data for rendering
 */
const getTransformedObservableCollection = (
    obsAccounts: AccountPermissionDTO[],
): ObservableAccountTileInputDataProps[] => {
    if (!obsAccounts) return [];
    return obsAccounts.map((data) => {
        return {
            accName: data.accountName,
            accType: data.accountType,
            roleType: data.roleType,
        };
    });
};

/**
 * Transforms user and account data into a collection of user tiles for display.
 * Combines user details with their associated accounts and roles.
 * @param account - Array of accounts owned by the grantor
 * @param userDetails - Array of user details to transform
 * @returns Array of user tile props containing combined account and user information
 */
const getTransformedUsersCollection = (account: OwnAccountDTO[], userDetails: UserDetailsDto[]) => {
    const users: UserTileInputDataProps[] = [];
    if (userDetails?.length && account?.length) {
        userDetails.forEach((detail) => {
            const accountsByPoid = getAccountsByClientPoid(account, detail.clientPoid);
            accountsByPoid.forEach((accountByPoid) => {
                const isGranteeOnAccount = accountByPoid?.grantees?.some(
                    (grantee) => grantee.clientPoid === detail.clientPoid,
                );

                if (accountByPoid && isGranteeOnAccount) {
                    const userFromAccount: GranteeDTO | RegisteredRoleDTO = getUserByClientPoid(
                        accountByPoid.grantees,
                        accountByPoid.owners,
                        detail.clientPoid,
                    );

                    // Check if user is an HSG recipient (IPHR or IPIR)
                    const isHsgRecipient = checkIsHsgRecipient(
                        accountByPoid.grantees,
                        detail.clientPoid,
                    );

                    const items: UserTileInputDataProps = {
                        accId: accountByPoid.accountId,
                        accName: accountByPoid.accountName,
                        accType: accountByPoid.accountType,

                        roleType: userFromAccount?.roleType ?? '',
                        usrName: constructUserName(detail),
                        isHsgRecipient: isHsgRecipient,
                        poid: detail.clientPoid,
                    };

                    if (userFromAccount && 'accessLevel' in userFromAccount) {
                        items.accessLevel = userFromAccount.accessLevel;
                    }

                    users.push(items);
                }
            });
        });
    }
    return users;
};

/**
 * Filters out users who are already grantees or co-owners on any account.
 * @param userDetails - Array of all user details
 * @param grantees - Array of existing grantees to filter out
 * @param coOwners - Array of existing co-owners to filter out
 * @returns Array of users who are not grantees or co-owners
 */
const filterOutGranteesOrCoOwnersOnAccountFromUsers = (
    userDetails: UserDetailsDto[] | undefined,
    grantees: GranteeDTO[] | undefined,
    coOwners: RegisteredRoleDTO[] | undefined,
): UserDetailsDto[] => {
    if (!userDetails || userDetails.length === 0) {
        return [];
    } else if ((!grantees || grantees.length === 0) && (!coOwners || coOwners.length === 0)) {
        return userDetails;
    }

    return userDetails.filter(
        (userDetail: UserDetailsDto) =>
            userDetail.lastName?.trim() &&
            !grantees.find((grantee: GranteeDTO) => grantee.clientPoid === userDetail.clientPoid) &&
            !coOwners.find(
                (coOwner: RegisteredRoleDTO) => coOwner.clientPoid === userDetail.clientPoid,
            ),
    );
};

/**
 * Returns list of users that filtered using grantees
 * Keeps only users who are existing grantees and enriches them with access level information.
 * @param userDetails - Array of all user details
 * @param grantees - Array of grantees to match against
 * @param warn - Logger function for warnings
 * @returns Array of users who are grantees, with access level added to each user
 */
const filterOutGranteesOnAccountFromUsers = (
    userDetails: UserDetailsDto[] | undefined,
    grantees: GranteeDTO[] | undefined,
    warn: (message: string) => void,
): UserDetailsDto[] => {
    if (!userDetails || userDetails.length === 0) {
        return [];
    } else if (!grantees || grantees.length === 0) {
        return userDetails;
    }

    return userDetails
        .filter((user: UserDetailsDto) =>
            grantees.some((grantee: GranteeDTO) => grantee.clientPoid == user.clientPoid),
        )
        .map((user: UserDetailsDto) => ({
            ...user,
            accessLevel: getAccessLevel(
                [grantees.find((grantee: GranteeDTO) => grantee.clientPoid == user.clientPoid)],
                user.clientPoid,
                warn,
            ),
        }));
};


/**
 * Checks if a user is already an existing grantee.
 * @param user - User to check
 * @param userDetails - Array of existing users to check against
 * @returns True if user exists in the userDetails array
 */
const isExistingGrantee = (user: UserDetailsDto, userDetails: UserDetailsDto[]): boolean => {
    return !!userDetails.find((usr) => usr.clientPoid === user.clientPoid);
};

/**
 * Determines the access level type based on a grantee's role type.
 * Maps role types (GIPR, GLAA, etc.) to access levels (INFORMATION_ONLY, LIMITED_ACCESS, FULL_AUTHORITY).
 * @param grantees - Array of grantees to search
 * @param clientPoid - The client POID to find
 * @param warn - Logger function for warnings
 * @returns The access type selection or null if grantee not found
 */
const getAccessLevel = (
    grantees: Array<GranteeDTO>,
    clientPoid: string,
    warn?: (message: string) => void,
): AccessTypeSelection | null => {
    const grantee: GranteeDTO = grantees.find((grantee) => grantee.clientPoid === clientPoid);

    if (!grantee) {
        warn?.(
            `Can't find grantee for revoke access for grantees: ${JSON.stringify(grantees)} and clientPoid: ${clientPoid}`,
        );
        return null;
    }

    const roleType = grantee.roleType;
    switch (roleType) {
        case RoleType.GIPR:
            return AccessTypeSelection.INFORMATION_ONLY;
        case RoleType.GLAA:
            return AccessTypeSelection.LIMITED_ACCESS;
        default:
            return AccessTypeSelection.FULL_AUTHORITY;
    }
};

/**
 * Handles access type and client type updates based on API response data.
 * Orchestrates store updates and error handling for the grantee flow.
 *
 * @param data - Response data from token validation or almost-there API
 * @param store - NgRx store instance for dispatching actions
 * @param errorService - Service for displaying error pages
 * @param isNonVgClientRoute - Flag indicating if this is a non-Vanguard client route
 *
 * @remarks
 * This function performs several critical operations:
 * 1. Validates that response data contains required properties
 * 2. Handles special case of grantor login detection (redirects to login)
 * 3. Dispatches store actions to update access type and client type
 * 4. Shows appropriate error pages based on error codes
 */
const handleAccessTypeAndClientTypeUpdate = (
    data: TokenValidationResponse | AlmostThereResponse,
    store: Store,
    errorService: ErrorService,
    isNonVgClientRoute?: boolean,
): void => {
    if (!data || !Object.hasOwn(data, 'error')) {
        errorService.showError(ErrorPage.TECH_DIFF);
        return;
    }

    if (Number(data.error) === PERMISSION_REQUEST_ERRORS.GRANTOR_LOGIN_DETECTED) {
        redirectToLogon();
        return;
    }

    let clientType: ClientTypes;
    if (isNonVgClientRoute) {
        clientType = ClientTypes.NON_VGCLIENT;
    } else {
        clientType = data.clientType ?? ClientTypes.UNKNOWN;
    }

    store.dispatch(updateAccessTypeInGranteeFlow(data.accessLevel));
    store.dispatch(updateClientTypeInGranteeFlow(clientType));

    if ('isMultiAccount' in data) {
        store.dispatch(updateIsMultiAccountInGranteeFlow({ isMultiAccount: data.isMultiAccount ?? false }));
    }

    if (Object.hasOwn(errorPageMap, data.error)) {
        errorService.showError(errorPageMap[data.error]);
        return;
    }
};

/**
 * Redirects the user to the logon page with the current URL as the target.
 * Used when grantor login is detected in a grantee flow.
 */
const redirectToLogon = (): void => {
    const targetUrl = encodeURIComponent(`${environment.APP_URL}${window.location.pathname}`);
    window.location.href = `${environment.LOGON_REDIRECT_URL}${targetUrl}&authenticate=true`;
};

export default {
    extractUsersFromAccountData,
    getAccountsByClientPoid,
    getUniqueGrantees,
    getTransformedUsersCollection,
    getTransformedObservableCollection,
    constructUserName,
    filterOutGranteesOrCoOwnersOnAccountFromUsers,
    filterUsersToExistingGranteesAcrossAccounts,
    getContactPersonType,
    isExistingGrantee,
    filterOutGranteesOnAccountFromUsers,
    getAccessLevel,
    handleAccessTypeAndClientTypeUpdate,
};
